import {
  Checkbox,
  FormControlLabel,
  Grid,
  IconButton,
  SelectChangeEvent,
  Tooltip,
  Typography,
  useEventCallback,
} from '@mui/material';
import { Fragment, memo, useEffect, useState } from 'react';
import { PmiInputFile } from 'shell/components/shared/file-input';
import DeleteIcon from '@mui/icons-material/Delete';
import { PracticeEditDocumentVM } from 'practice/view-models/practice-edit-document';
import ListTypeDocument from './list-type-document';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { __PRACTICE_API_URL__ } from 'shell/constants/environment';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { toast } from 'react-toastify';
import { TypeFile } from 'practice/components/detail/form/accordion-attachments/form';
import { HttpResponse } from 'shell/models/http-response';
import { AttachmentsData } from 'shell/models/practice';
import { Attachment } from 'shell/models/attachment';
import { readFileAsBase64 } from 'shell/utils/file-to-base64';
import { PracticeDraftBEStep } from 'practice/constants/practice-management-step';

interface FormProps {
  disabled?: boolean;
  onChange: (
    key: keyof PracticeEditDocumentVM,
    value: PracticeEditDocumentVM[keyof PracticeEditDocumentVM]
  ) => void;
  onChanges: (values: PracticeEditDocumentVM) => void;
  values?: PracticeEditDocumentVM;
  codeEnte?: string;
  yearId?: string;
  typeRegister?: string;
  progressive?: string;
  maxActiveStep: number;
  requiredDocuments?: Attachment[];
  consensoOspitante?: boolean;
}

/**
 * Form component for practice edit document form
 * @param {object} props - component props
 * @param {boolean} props.disabled - is form disabled
 * @param {(key: string, value: string) => void} props.onChange - handler for change event
 * @param {(values: PracticeEditDocumentVM) => void} props.onChanges - handler for form changes
 * @param {PracticeEditDocumentVM} props.values - form values
 * @param {string} props.codeEnte - code of the municipality
 * @param {string} props.yearId - year of the practice
 * @param {string} props.typeRegister - type of register
 * @param {string} props.progressive - progressive number
 * @param {number} props.maxActiveStep - max active step
 * @param {string[]} props.requiredDocuments - list of required documents
 * @returns {React.ReactElement} - form component
 */
const FormComponent = ({
  disabled,
  onChange,
  values,
  onChanges,
  codeEnte,
  yearId,
  typeRegister,
  progressive,
  maxActiveStep,
  requiredDocuments,
  consensoOspitante,
}: FormProps): React.ReactElement => {
  const [errorListTypeDocument, setErrorListTypeDocument] = useState<boolean>(false);

  const insertData = useHttpClient(`${__PRACTICE_API_URL__}/pratiche/allegati`, {
    options: { method: 'POST' },
  });

  const deleteData = useHttpClient(`${__PRACTICE_API_URL__}/pratiche/allegati`, {
    options: { method: 'DELETE' },
  });

  useEffect(() => {
    if (values?.isNotDocument) {
      // reset files
      onChanges({
        ...(values || {}),
        files: undefined,
        typeDocument: undefined,
        addedFiles: undefined,
      });
    }
  }, [values?.isNotDocument]);

  /**
   * Handler for add file event
   * @param {Array<File>} files - files to be added
   * If values.typeDocument is not set, it will set an error for the form
   * It will read the file as base64 string and insert a record on allegato table
   * If the operation is successful, it will update the form values and show a success toast
   */
  const handleAddFile = useEventCallback(async (files: Array<File>) => {
    if (!values?.typeDocument) {
      // inserire tutti i campi obbligatori
      setErrorListTypeDocument(true);

      return;
    }

    const filesBase64 = await Promise.all(files.map(async (file) => await readFileAsBase64(file)));

    // inserire un record sulla tabella allegato
    const data: Attachment = {
      codiceEnte: codeEnte || undefined,
      tipoRegistro: typeRegister || undefined,
      idAnnoPratica: yearId ? Number.parseInt(yearId, 10) : undefined,
      progressivoPratica: progressive ? Number.parseInt(progressive, 10) : undefined,
      nomeFile: files[0].name || undefined,
      idTipoFormato: files[0].type?.includes('pdf') ? 1 : 99,
      idTipoAllegato: values.typeDocument as unknown as number,
      contenutoAllegato: filesBase64[0] as string,
      tipoOrigine: 'A',
      stepBozza:
        maxActiveStep > PracticeDraftBEStep.Irricevibilita
          ? maxActiveStep
          : PracticeDraftBEStep.Irricevibilita,
    };

    (insertData({ data }) as ResponsePromise<HttpResponse<AttachmentsData>>).then((response) => {
      toast.success('Operazione eseguita con successo');

      const newFiles: Array<TypeFile> = values?.files?.slice() || [];

      const filesInsert: Array<TypeFile> = [
        {
          ...files[0],
          raw: filesBase64[0] as string,
          idAllegato: response?.data.payload.key?.idAllegato,
          name: response?.data.payload.nomeFile || '',
        },
      ];

      onChanges({
        ...(values || {}),
        files: newFiles.concat(filesInsert),
        typeDocument: undefined,
      });
    });
  });

  /**
   * Handler for add required file event
   * @param {Array<File>} files - files to be added
   * @param {string} el - element to which the files are associated
   * If values.typeDocument is not set, it will set an error for the form
   * It will read the file as base64 string and insert a record on allegato table
   * If the operation is successful, it will update the form values and show a success toast
   */
  const handleAddFRequiredile = useEventCallback(async (files: Array<File>, el: Attachment) => {
    const filesBase64 = await Promise.all(files.map(async (file) => await readFileAsBase64(file)));

    // inserire un record sulla tabella allegato
    const data: Attachment = {
      codiceEnte: codeEnte || undefined,
      tipoRegistro: typeRegister || undefined,
      idAnnoPratica: yearId ? Number.parseInt(yearId, 10) : undefined,
      progressivoPratica: progressive ? Number.parseInt(progressive, 10) : undefined,
      nomeFile: files[0].name || undefined,
      idTipoAllegato: el.key?.idTipoAllegato || el.idTipoAllegato,
      idTipoFormato: files[0].type?.includes('pdf') ? 1 : 99,
      contenutoAllegato: filesBase64[0] as string,
      tipoOrigine: 'C',
      stepBozza:
        maxActiveStep > PracticeDraftBEStep.Irricevibilita
          ? maxActiveStep
          : PracticeDraftBEStep.Irricevibilita,
    };

    (insertData({ data }) as ResponsePromise<HttpResponse<AttachmentsData>>).then((response) => {
      toast.success('Operazione eseguita con successo');

      const newFiles: Array<TypeFile> =
        (
          (values?.addedFiles || {})[el.descrizioneTipoAllegato || ''] as Array<TypeFile>
        )?.slice() || [];

      const filesInsert: Array<TypeFile> = [
        {
          ...files[0],
          raw: filesBase64[0] as string,
          idAllegato: response?.data.payload.key?.idAllegato,
          name: response?.data.payload.nomeFile || '',
          idTipoAllegato: el.key?.idTipoAllegato?.toString(),
        },
      ];

      onChanges({
        ...(values || {}),
        addedFiles: {
          ...values?.addedFiles,
          [el.descrizioneTipoAllegato || '']: newFiles.concat(filesInsert),
        },
        typeDocument: undefined,
      });
    });
  });

  /**
   * Deletes a required file from the list of added files
   * @param {number} indexCurr - index of the file to be deleted
   * @param {string} el - element to which the files are associated
   */
  const onTrashPressRequiredFile = useEventCallback((indexCurr: number, el: Attachment) => {
    const file: TypeFile | undefined = (
      ((values?.addedFiles || {})[el.descrizioneTipoAllegato || ''] as Array<TypeFile>) || []
    ).find((_, index) => index === indexCurr);

    (deleteData({ url: `/${file?.idAllegato || ''}` }) as ResponsePromise<void>).then(() => {
      toast.success('Operazione eseguita con successo');

      const newFiles: Array<TypeFile> =
        (
          ((values?.addedFiles || {})[el.descrizioneTipoAllegato || ''] as Array<TypeFile>) || []
        ).filter((_, index) => index !== indexCurr) || [];

      onChange('addedFiles', {
        ...values?.addedFiles,
        [el.descrizioneTipoAllegato || '']: newFiles,
      });
    });
  });

  /**
   * Deletes a file from the list of files
   * @param {number} indexCurr - index of the file to be deleted
   */
  const onTrashPress = useEventCallback((indexCurr: number) => {
    const file: TypeFile | undefined = (values?.files || []).find(
      (_, index) => index === indexCurr
    );

    (deleteData({ url: `/${file?.idAllegato || ''}` }) as ResponsePromise<void>).then(() => {
      toast.success('Operazione eseguita con successo');

      const newFiles: Array<TypeFile> =
        (values?.files || []).filter((_, index) => index !== indexCurr) || [];

      onChange('files', newFiles);
    });
  });

  /**
   * Handles the change event of the select element for type document
   * @param {SelectChangeEvent<string>} event - the change event
   */
  const handleListTypeDocumentChange = useEventCallback((event: SelectChangeEvent<string>) => {
    onChange('typeDocument', event.target.value);
    setErrorListTypeDocument(false);
  });

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <FormControlLabel
          control={
            <Checkbox
              id="isTranscribed"
              checked={values?.isNotDocument || false}
              onChange={() => onChange('isNotDocument', !values?.isNotDocument)}
              inputProps={{ 'aria-label': 'Non sono presenti documenti da allegare alla pratica' }}
              disabled={disabled}
            />
          }
          label="Non sono presenti documenti da allegare alla pratica"
        />
      </Grid>
      {requiredDocuments?.map((el) => (
        <Fragment key={el.descrizioneTipoAllegato}>
          <Grid item xs={12} key={el.descrizioneTipoAllegato}>
            <Typography
              component="span"
              fontWeight={el.key?.idTipoAllegato === 10 && consensoOspitante ? 800 : undefined}
            >
              {el.descrizioneTipoAllegato}
              {el.key?.idTipoAllegato === 10 && consensoOspitante ? '*' : ''}
            </Typography>
            &nbsp;
            <PmiInputFile
              files={
                ((values?.addedFiles || {})[
                  el.descrizioneTipoAllegato as string
                ] as Array<TypeFile>) || []
              }
              onChange={(files) => handleAddFRequiredile(files, el)}
              color="cdmBrandThird"
              multiple
              hideList
              disabled={disabled || values?.isNotDocument}
              accept="application/pdf, image/*"
            />
          </Grid>
          <Grid item xs={12}>
            {values?.addedFiles?.[el.descrizioneTipoAllegato as string]?.map((file, index) => (
              <Grid container spacing={2} item xs={12} key={`file-${file.name}-${index}`}>
                <Grid item xs={12} md={4}>
                  <Tooltip arrow title={values.descriptions ? values.descriptions[index] : ''}>
                    <a download={file.name} href={file.raw}>
                      {file.name}
                    </a>
                  </Tooltip>
                </Grid>
                <Grid item xs={12} md={5}>
                  <IconButton
                    color="error"
                    aria-label="eliminare documento allegato"
                    component="span"
                    onClick={() => onTrashPressRequiredFile(index, el)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Grid>
              </Grid>
            ))}
          </Grid>
        </Fragment>
      ))}
      <Grid item xs={12} md={10}>
        <ListTypeDocument
          value={values?.typeDocument || ''}
          onChange={handleListTypeDocumentChange}
          disabled={disabled || values?.isNotDocument}
          error={errorListTypeDocument}
        />
      </Grid>
      <Grid item xs={12}>
        Carica documento&nbsp;
        <PmiInputFile
          files={values?.files}
          onChange={handleAddFile}
          color="cdmBrandThird"
          multiple
          hideList
          disabled={disabled || values?.isNotDocument}
          accept="application/pdf, image/*"
        />
      </Grid>
      <Grid item xs={12}>
        {values?.files?.map((file, index) => (
          <Grid container spacing={2} item xs={12} key={`file-${file.name}-${index}`}>
            <Grid item xs={12} md={4}>
              <Tooltip arrow title={values.descriptions ? values.descriptions[index] : ''}>
                <a download={file.name} href={file.raw}>
                  {file.name}
                </a>
              </Tooltip>
            </Grid>
            <Grid item xs={12} md={5}>
              <IconButton
                color="error"
                aria-label="eliminare documento allegato"
                component="span"
                onClick={() => onTrashPress(index)}
                disabled={disabled}
              >
                <DeleteIcon />
              </IconButton>
            </Grid>
          </Grid>
        ))}
      </Grid>
    </Grid>
  );
};

FormComponent.displayName = 'Practice Detail - Form - accordion contacts - form';

const Form = memo(FormComponent);

export default Form;
